import { useState } from 'react';
import { ShieldAlert, ChevronDown, ChevronRight } from 'lucide-react';
import type { StrategicRecommendation } from '../../types';

interface PlanAdvisoriesProps {
  recommendations?: StrategicRecommendation[];
}

/**
 * Renders plan-level optimizer advisories (Phase 2.2 approval assumptions,
 * Phase 3.3 repeat-enrollment guidance) surfaced via
 * optimization_status.recommendations. These explain assumptions/shortfalls
 * the auto-plan can't resolve for the student — previously computed but
 * never shown (the "visible warning" half of owner decisions D2/D3).
 */
const PlanAdvisories = ({ recommendations }: PlanAdvisoriesProps) => {
  const [expanded, setExpanded] = useState(true);
  const recs = recommendations || [];
  if (recs.length === 0) return null;

  return (
    <div className="mb-3 rounded-card border border-status-warning/20 bg-status-warning/5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
        aria-expanded={expanded}
      >
        <ShieldAlert className="h-4 w-4 flex-shrink-0 text-status-warning" />
        <span className="text-xs font-medium text-status-warning-fg">
          {recs.length === 1 ? '1 plan note' : `${recs.length} plan notes`}
        </span>
        {expanded ? (
          <ChevronDown className="ml-auto h-3.5 w-3.5 text-ink-3" />
        ) : (
          <ChevronRight className="ml-auto h-3.5 w-3.5 text-ink-3" />
        )}
      </button>

      {expanded && (
        <ul className="space-y-1.5 px-3 pb-2.5">
          {recs.map((rec, i) => (
            <li key={i} className="text-xs text-ink-2">
              <span className="font-medium text-ink-1">{rec.title}</span>
              {rec.description ? (
                <span className="text-ink-2"> — {rec.description}</span>
              ) : null}
              {rec.action ? (
                <span className="block text-ink-3">{rec.action}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlanAdvisories;
